/**
 * Screen 8 — Match result.
 *
 * Wireframe shape:
 *   - violet "YOU WON / DRAW / YOU LOST" hero with the headline ₦ amount
 *   - row of 3 stat cards (placeholders for XP/Gems/Streak until Phase 11)
 *   - "Final standings" rows
 *   - Money breakdown (pool / rake / payout) when this was a paid contest
 *   - per-question breakdown (collapsed by default to keep the screen short)
 *   - Rematch (solo = instant new game; duel = request → poll → accept/decline) +
 *     Share CTAs
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { questionImageHtml } from '../lib/question_image.js';
import { shareWin } from '../lib/share_win.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';

const REMATCH_POLL_MS = 2000;

const RESULT_LABEL = {
  win:     { text: 'YOU WON',  tone: 'violet' },
  loss:    { text: 'YOU LOST', tone: 'dark'   },
  draw:    { text: 'DRAW',     tone: 'dark'   },
  forfeit: { text: 'FORFEIT',  tone: 'dark'   },
  none:    { text: 'DONE',     tone: 'dark'   },
};

export async function renderMatchResult(root) {
  const parts = window.location.pathname.split('/');
  const matchId = parts[parts.length - 2];

  root.innerHTML = `
    <div class="appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Match result</div>
    </div>
    <div id="result-page">
      <div class="pad"><div class="skel"></div><div class="skel"></div></div>
    </div>
  `;

  let result;
  try {
    result = await api(`/match/${matchId}/result`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    toast.fromError(err);
    document.getElementById('result-page').innerHTML =
      `<div class="pad"><div class="card" style="color:var(--pink-t)">${escape(err.message)}</div></div>`;
    return;
  }

  const me = auth.getMe();
  const myUserId = me?.id;
  const myPlayer = (result.players || []).find((p) => p.user_id === myUserId) ||
    result.players?.[0] || null;
  const opponent = (result.players || []).find((p) => p.user_id !== myUserId) || null;
  const myLabel = RESULT_LABEL[myPlayer?.result || 'none'];

  document.getElementById('result-page').innerHTML = `
    ${renderHero(result, myPlayer, myLabel)}
    <div class="pad">
      ${renderRewardCards(result, myPlayer)}
      ${renderContestSummary(result.contest)}
      <h3 class="sec">Final standings</h3>
      ${renderStandings(result, myUserId)}
      <details>
        <summary style="cursor:pointer;color:var(--ink-2);font-size:13px;font-weight:600;margin:12px 0;">
          Question breakdown
        </summary>
        ${renderBreakdown(result, myPlayer, opponent)}
      </details>
      <div class="row" style="gap:9px;margin-top:14px;">
        <button type="button" class="btn teal" id="rematch-btn" style="flex:1;">Rematch</button>
        ${result.mode === 'duel' && myPlayer?.result === 'win'
          ? `<button type="button" class="btn ghost" id="share-btn" style="flex:1;">📣 Share win</button>`
          : ''}
      </div>
      <div id="rematch-banner"></div>
      <div class="cta-row" style="margin-top:8px;">
        <a href="/wallet" data-link class="btn ghost">Open wallet</a>
        <a href="/" data-link class="btn ghost">Home</a>
      </div>
    </div>
  `;

  setupRematch({ matchId, mode: result.mode, myPlayer });

  // Share-win flow — only mounted for duels the caller actually won
  // (the button HTML is conditional in the render above). Solo wins
  // skip share because a solo "win" is trivial — every solo session
  // ends with the player on top.
  const shareBtn = document.getElementById('share-btn');
  if (shareBtn) {
    shareBtn.addEventListener('click', async () => {
      shareBtn.disabled = true;
      const original = shareBtn.textContent;
      shareBtn.textContent = 'Preparing…';
      try {
        // Free duel → amount=0 → card renders "Winner!" headline
        // instead of an ₦ figure. Paid duel → the player's contest
        // payout. Subtitle leans on the opponent's display name when
        // we have it.
        const amountKobo = Number(result.contest?.your_payout_kobo || 0);
        const opponentName = opponent
          ? (opponent.username || `Player ${(opponent.user_id || '').slice(-4)}`)
          : null;
        await shareWin({
          kind: 'duel',
          amountKobo,
          title: opponentName ? `vs ${opponentName}` : '1-on-1 Duel',
          subtitle:
            myPlayer && opponent
              ? `${myPlayer.score ?? 0} pts to ${opponent.score ?? 0}`
              : undefined,
        });
      } finally {
        shareBtn.disabled = false;
        shareBtn.textContent = original;
      }
    });
  }
}

function renderHero(result, myPlayer, myLabel) {
  const heroClass = myLabel.tone === 'violet' ? 'hero violet' : 'hero dark';
  const won = myPlayer?.result === 'win';
  const amount = won && result.contest
    ? `<div style="font-family:var(--font-display);font-size:38px;font-weight:700;color:#fff;margin-top:6px;">
         + ${escape(formatKobo(result.contest.your_payout_kobo))}
       </div>
       <div style="font-size:12px;font-weight:500;opacity:0.9;margin-top:2px;">
         credited to your wallet instantly
       </div>`
    : `<div style="font-family:var(--font-display);font-size:38px;font-weight:700;color:#fff;margin-top:6px;">
         ${myPlayer?.score ?? 0} pts
       </div>
       <div style="font-size:12px;font-weight:500;opacity:0.9;margin-top:2px;">
         ${myPlayer?.correct ?? 0} / ${result.questions.length} correct
       </div>`;
  return `
    <div class="${heroClass} center flush" style="padding:30px 18px 22px;">
      <div style="font-family:var(--font-display);font-size:14px;letter-spacing:2px;opacity:0.85;">
        ${myLabel.text}
      </div>
      ${amount}
    </div>
  `;
}

function renderRewardCards(_result, _myPlayer) {
  // Placeholder XP / Gems / Streak — Phase 11 will replace the numbers.
  return `
    <div class="row" style="gap:9px;">
      ${rewardCard('+0', 'XP', 'var(--gold-t)')}
      ${rewardCard('+0', 'Gems', 'var(--blue-t)')}
      ${rewardCard('1', 'Streak', 'var(--flame)')}
    </div>
  `;
}

function rewardCard(value, label, color) {
  return `
    <div class="card center" style="flex:1;margin:0;padding:12px;">
      <div style="font-family:var(--font-display);font-size:17px;font-weight:600;color:${color};">
        ${escape(value)}
      </div>
      <div style="font-size:10px;color:var(--mute);font-weight:600;text-transform:uppercase;letter-spacing:0.4px;margin-top:2px;">
        ${escape(label)}
      </div>
    </div>
  `;
}

function renderContestSummary(contest) {
  if (!contest) return '';
  const youWon = contest.your_entry_status === 'won';
  const refunded = contest.your_entry_status === 'refunded';
  const note = refunded
    ? '<div class="note teal">Stake refunded — no rake taken.</div>'
    : '';
  return `
    <h3 class="sec">Money</h3>
    <div class="card">
      <div class="row">
        <div class="stat">
          <div class="v">${escape(formatKobo(contest.pool_kobo))}</div>
          <div class="k">Pool</div>
        </div>
        <div class="stat">
          <div class="v">${escape(formatKobo(contest.rake_kobo))}</div>
          <div class="k">Rake</div>
        </div>
        <div class="stat">
          <div class="v" style="color:var(--teal-t);">${escape(formatKobo(contest.payout_kobo))}</div>
          <div class="k">${youWon ? 'You won' : 'Paid out'}</div>
        </div>
      </div>
      ${note}
    </div>
  `;
}

function renderStandings(result, myUserId) {
  const players = (result.players || [])
    .slice()
    .sort((a, b) => b.score - a.score || a.total_ms - b.total_ms);
  return players.map((p, i) => {
    const rank = i + 1;
    const isYou = p.user_id === myUserId;
    const payoutTag = result.contest && p.user_id === myUserId && result.contest.your_payout_kobo > 0
      ? `<span class="tag gold" style="margin-left:9px;">+${escape(formatKobo(result.contest.your_payout_kobo))}</span>`
      : '';
    return `
      <div class="card" style="padding:11px;${isYou ? 'border-color:var(--violet);' : ''}">
        <div class="row">
          <div class="ic ${rank === 1 ? 'gold' : ''}" style="${rank === 1 ? 'color:#3a2a00;font-weight:700;' : ''}">${rank}</div>
          <div class="av sm">${escape((p.user_id || '?').slice(0, 1).toUpperCase())}</div>
          <div class="grow">
            <b style="font-size:13px;color:#fff;font-weight:600;">
              ${isYou ? 'You' : escape(p.username || 'Player ' + (p.seat + 1))}
            </b>
            <div style="font-size:11px;color:var(--mute);font-weight:500;">
              ${p.correct} correct · ${(p.total_ms / 1000).toFixed(1)}s
            </div>
          </div>
          <b style="font-family:var(--font-display);color:#fff;font-weight:600;">${p.score} pts</b>
          ${payoutTag}
        </div>
      </div>
    `;
  }).join('');
}

function renderBreakdown(result, myPlayer, opponent) {
  const isSolo = result.mode === 'solo';
  return (result.questions || [])
    .map((q) => renderQuestionRow(q, myPlayer, opponent, isSolo))
    .join('');
}

function renderQuestionRow(q, myPlayer, opponent, isSolo) {
  const mySeat = myPlayer?.seat ?? 0;
  const myAnswer = (q.answers || [])[mySeat] || null;
  const oppSeat = opponent?.seat;
  const oppAnswer = oppSeat != null ? (q.answers || [])[oppSeat] || null : null;
  const correctChoice = q.choices[q.answer_index] ?? '?';
  const oppBit = isSolo ? '' : ` · Opp ${answerBadge(oppAnswer)}`;
  return `
    <div class="card" style="padding:11px;">
      ${questionImageHtml(q.image_url, { lazy: true, maxHeightPx: 140 })}
      <div style="font-size:12.5px;color:#fff;font-weight:600;line-height:1.4;white-space:pre-wrap;">Q${q.order + 1}. ${escape(q.prompt)}</div>
      <div style="font-size:11px;color:var(--mute);font-weight:500;margin-top:6px;">
        ✓ ${escape(correctChoice)} · You ${answerBadge(myAnswer)}${oppBit}
      </div>
    </div>
  `;
}

function answerBadge(ans) {
  if (!ans) return '<span class="status-pill warn">none</span>';
  if (ans.correct) return `<span class="status-pill ok">✓ ${(ans.ms / 1000).toFixed(1)}s</span>`;
  return `<span class="status-pill bad">✗ ${(ans.ms / 1000).toFixed(1)}s</span>`;
}

// -- Rematch -----------------------------------------------------------------
//
// Solo  → tap Rematch → POST /match/{id}/solo-rematch → navigate to new match.
// Duel  → tap Rematch → POST /match/{id}/rematch (status=pending).
//   - The OTHER seat sees a notification banner (we GET /rematch on a poll)
//     and can Accept or Decline.
//   - When status flips to "accepted" both clients navigate to the new match.
//   - When status flips to "declined" the requester sees the banner and stops.
//
// The poll runs whenever this page is visible and the duel is finished — it's
// cheap (one row by id) and self-cancels once we leave the page.

function setupRematch({ matchId, mode, myPlayer }) {
  const btn = document.getElementById('rematch-btn');
  if (!btn) return;

  if (mode === 'solo') {
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      btn.textContent = 'Starting…';
      try {
        const fresh = await api(`/match/${matchId}/solo-rematch`, { method: 'POST' });
        navigate(`/match/${fresh.id}`);
      } catch (err) {
        btn.disabled = false;
        btn.textContent = 'Rematch';
        toast.fromError(err);
      }
    });
    return;
  }

  // Duel rematch handshake. `myState` tracks what this client has done so the
  // banner can show the right message without flickering.
  const banner = document.getElementById('rematch-banner');
  const mySeat = myPlayer?.seat ?? null;
  let pollTimer = null;
  let stopped = false;
  let lastStatus = 'none';
  let lastRequestedBy = null;

  const stop = () => {
    stopped = true;
    if (pollTimer) { clearTimeout(pollTimer); pollTimer = null; }
  };

  // If the user leaves the page (back nav, hash change), kill the poller.
  window.addEventListener('popstate', stop, { once: true });

  const render = (state) => {
    if (!banner) return;
    const { status, requested_by_seat, new_match_id } = state;
    lastStatus = status;
    lastRequestedBy = requested_by_seat;

    if (status === 'accepted' && new_match_id) {
      banner.innerHTML = `
        <div class="note teal" style="margin-top:9px;">
          Rematch on! Loading the new match…
        </div>
      `;
      stop();
      // Tiny delay so both sides see the "loading" flash and don't fight the
      // navigation animation.
      setTimeout(() => navigate(`/match/${new_match_id}`), 400);
      return;
    }

    if (status === 'declined') {
      const declinedByMe = requested_by_seat != null && requested_by_seat !== mySeat;
      if (declinedByMe) {
        banner.innerHTML = `
          <div class="note dark" style="margin-top:9px;">You declined the rematch.</div>
        `;
      } else {
        banner.innerHTML = `
          <div class="note pink" style="margin-top:9px;">
            Your opponent declined the rematch.
          </div>
        `;
      }
      btn.disabled = true;
      btn.textContent = 'Rematch declined';
      stop();
      return;
    }

    if (status === 'pending' && requested_by_seat === mySeat) {
      // I asked — waiting for the other side.
      btn.disabled = true;
      btn.textContent = 'Waiting for opponent…';
      banner.innerHTML = `
        <div class="note teal" style="margin-top:9px;">
          Rematch requested. Waiting for your opponent…
        </div>
      `;
      return;
    }

    if (status === 'pending' && requested_by_seat != null && requested_by_seat !== mySeat) {
      // Other side asked — show accept/decline.
      btn.disabled = true;
      btn.textContent = 'Opponent wants a rematch';
      banner.innerHTML = `
        <div class="card" style="background:var(--violet-bg);border-color:var(--violet);margin-top:10px;">
          <div style="font-family:var(--font-display);font-size:14px;color:#fff;font-weight:600;">
            Your opponent wants a rematch
          </div>
          <div style="font-size:12px;color:var(--ink-2);margin:4px 0 9px;">
            Same category, new questions, free game.
          </div>
          <div class="row" style="gap:8px;">
            <button type="button" class="btn teal" id="rematch-accept" style="flex:1;">Accept</button>
            <button type="button" class="btn ghost" id="rematch-decline" style="flex:1;">Decline</button>
          </div>
        </div>
      `;
      document.getElementById('rematch-accept')?.addEventListener('click', async () => {
        try {
          const res = await api(`/match/${matchId}/rematch/accept`, { method: 'POST' });
          render(res);
        } catch (err) { toast.fromError(err); }
      });
      document.getElementById('rematch-decline')?.addEventListener('click', async () => {
        try {
          const res = await api(`/match/${matchId}/rematch/decline`, { method: 'POST' });
          render(res);
        } catch (err) { toast.fromError(err); }
      });
      return;
    }

    // status === 'none' — clear any leftover banner.
    banner.innerHTML = '';
    btn.disabled = false;
    btn.textContent = 'Rematch';
  };

  btn.addEventListener('click', async () => {
    btn.disabled = true;
    btn.textContent = 'Requesting…';
    try {
      const res = await api(`/match/${matchId}/rematch`, { method: 'POST' });
      render(res);
    } catch (err) {
      btn.disabled = false;
      btn.textContent = 'Rematch';
      toast.fromError(err);
    }
  });

  async function poll() {
    if (stopped) return;
    try {
      const res = await api(`/match/${matchId}/rematch`);
      // Avoid re-rendering the accept/decline card if nothing changed — it
      // would otherwise rebind buttons and wipe an in-flight tap.
      if (res.status !== lastStatus || res.requested_by_seat !== lastRequestedBy ||
          (res.status === 'accepted' && res.new_match_id)) {
        render(res);
      }
    } catch {
      // Match could have been GC'd or the network blinked — keep trying.
    }
    if (!stopped) pollTimer = setTimeout(poll, REMATCH_POLL_MS);
  }
  poll();
}
